"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, FileText, PhoneCall, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const benefits = [{
  id: 1,
  title: "Dedicated Field Specialists",
  description: "Application and technical support across Ireland and the UK."
}, {
  id: 2,
  title: "Full Qualification & IQ/OQ",
  description: "Complete validation packages for regulated laboratories."
}, {
  id: 3,
  title: "Rapid Instrument Servicing",
  description: "Preventative maintenance and lifecycle technical support."
}];
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="calltoaction" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28">
      {/* Background Decorative Element */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-40">
        <div className="h-[420px] w-[640px] rounded-full bg-accent/40 blur-3xl" />
      </div>

      <div className="page-container relative z-10">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center">
          {/* Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-4 py-1.5 text-xs font-medium text-secondary-foreground shadow-2xs">
            <ShieldCheck className="size-4 text-primary" />
            <span>Regulated Laboratory & Manufacturing Support</span>
          </div>

          {/* Main Heading */}
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Ready to enhance your analytical testing capabilities?
          </h2>

          {/* Supporting Copy */}
          <p className="mt-5 text-base text-muted-foreground sm:text-lg">
            Consult with our analytical instrumentation specialists to select the right
            technologies, ensure regulatory compliance, and support your instruments across their lifecycle.
          </p>

          {/* High-Contrast CTA Buttons */}
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button size="lg" asChild className="w-full font-medium shadow-md sm:w-auto">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Request a Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full font-medium sm:w-auto">
              <Link to="/ProductsPage" className="inline-flex items-center gap-2">
                <FileText className="size-4 text-muted-foreground" />
                <span>Browse Products</span>
              </Link>
            </Button>
          </div>

          {/* Direct Action Card */}
          <div className="mt-12 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="grid grid-cols-1 gap-6 text-left sm:grid-cols-3">
              {benefits.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Quick Direct Link / Contact Line */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-primary" />
              <span>Tailored instrumentation for pharma & biopharma</span>
            </div>
            <div className="flex items-center gap-1.5">
              <PhoneCall className="size-3.5 text-primary" />
              <Link to="/ContactPage" className="font-medium text-foreground hover:underline">
                Speak directly with an engineer
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}